import { useEffect } from 'react';
import Animated, {
    Easing,
    useAnimatedProps,
    useSharedValue,
    withDelay,
    withRepeat,
    withSequence,
    withTiming,
} from 'react-native-reanimated';
import Svg, { Path } from 'react-native-svg';
import { colorPrimitiveTokens, type ColorTokenKey, View } from 'smoothie';

const AnimatedPath = Animated.createAnimatedComponent(Path);

const toHex = (token: ColorTokenKey): string =>
    (colorPrimitiveTokens as Record<string, string>)[token.slice(1)] ?? token;

const AnimatedForkLine = ({
    loopProgress,
}: {
    loopProgress?: Animated.SharedValue<number>;
}) => {
    const localProgress = useSharedValue(0);
    const pathLength = 48;
    const dotSize = 24;

    useEffect(() => {
        if (loopProgress != null) return;
        localProgress.value = withRepeat(
            withSequence(
                withTiming(1, {
                    duration: 1000,
                    easing: Easing.inOut(Easing.ease),
                }),
                withDelay(500, withTiming(0, { duration: 0 })),
            ),
            -1,
            false,
        );
    }, [loopProgress]);

    const progress = loopProgress ?? localProgress;

    const forkTravel = pathLength + dotSize;

    const dotProps = useAnimatedProps(() => {
        const t = Math.min(progress.value, 1);
        return {
            strokeDashoffset: dotSize - t * forkTravel,
            strokeOpacity: 1 - t,
        };
    });

    return (
        <Svg
            width={48}
            height={12}
            viewBox="0 0 48 12"
            {...{ overflow: 'visible' }}
        >
            <Path
                d="M 0 6 L 48 6"
                stroke="white"
                strokeWidth={4}
                strokeLinecap="round"
                fill="none"
            />
            <AnimatedPath
                animatedProps={dotProps}
                d="M 48 6 L 0 6"
                stroke="#F6DC72"
                strokeWidth={4}
                strokeLinecap="round"
                strokeDasharray={`${dotSize} ${pathLength}`}
                fill="none"
            />
        </Svg>
    );
};

const DOT_COLORS: ColorTokenKey[] = [
    '$water',
    '$leafLight',
    '$lemon',
    '$petal',
];

const AnimatedForkProng = ({
    midY,
    yEnd,
    color,
    loopProgress,
    loopProgressA,
    loopProgressB,
    localProgress,
    threshold,
    range,
    filledCount,
    groupIndex,
    alwaysAnimate,
}: {
    midY: number;
    yEnd: number;
    color: string;
    loopProgress: Animated.SharedValue<number> | undefined;
    loopProgressA?: Animated.SharedValue<number>;
    loopProgressB?: Animated.SharedValue<number>;
    localProgress: Animated.SharedValue<number>;
    threshold: number;
    range: number;
    filledCount?: Animated.SharedValue<number>;
    groupIndex?: number;
    alwaysAnimate?: boolean;
}) => {
    const dotSize = 24;
    const controlPoly = 28 + Math.abs(midY - yEnd) + 20;
    const chord = Math.sqrt(48 * 48 + (midY - yEnd) * (midY - yEnd));
    const length = (controlPoly + chord) / 2;
    const travel = length + dotSize;

    const dotProps = useAnimatedProps(() => {
        if (filledCount != null && groupIndex != null) {
            if (alwaysAnimate) {
                if (filledCount.value + 1 < groupIndex) {
                    return { strokeDashoffset: dotSize, strokeOpacity: 0 };
                }
            } else if (
                filledCount.value !== groupIndex &&
                filledCount.value + 1 !== groupIndex
            ) {
                return { strokeDashoffset: dotSize, strokeOpacity: 0 };
            }
        }
        let t: number;
        if (
            loopProgressA != null &&
            loopProgressB != null &&
            groupIndex != null
        ) {
            if (
                alwaysAnimate &&
                filledCount != null &&
                filledCount.value >= groupIndex
            ) {
                // Retirement prongs in steady state: evaluate both SVs
                // and use whichever is currently producing a visible dot,
                // so the dot animates every iteration (not every other).
                const dpA = Math.max(0, loopProgressA.value - 1);
                const dpB = Math.max(0, loopProgressB.value - 1);
                const tA = Math.min(1, Math.max(0, (dpA - threshold) / range));
                const tB = Math.min(1, Math.max(0, (dpB - threshold) / range));
                // Prefer the SV mid-travel (0 < t < 0.99); if both qualify, pick earlier
                if (tA > 0 && tA < 0.99) {
                    t = tA;
                } else if (tB > 0 && tB < 0.99) {
                    t = tB;
                } else {
                    t = Math.max(tA, tB);
                }
            } else {
                const progress =
                    groupIndex % 2 === 0
                        ? loopProgressA.value
                        : loopProgressB.value;
                const dotPhase = Math.max(0, progress - 1);
                t = Math.min(1, Math.max(0, (dotPhase - threshold) / range));
            }
        } else if (loopProgress != null) {
            const dotPhase = Math.max(0, loopProgress.value - 1);
            t = Math.min(1, Math.max(0, (dotPhase - threshold) / range));
        } else {
            t = localProgress.value;
        }
        if (t >= 0.99) {
            return { strokeDashoffset: dotSize, strokeOpacity: 0 };
        }
        return {
            strokeDashoffset: dotSize - t * travel,
            strokeOpacity: t > 0 ? 1 : 0,
        };
    });

    const d = `M 0 ${midY} C 28 ${midY}, 28 ${yEnd}, 48 ${yEnd}`;

    return (
        <>
            <Path
                d={d}
                stroke="white"
                strokeWidth={4}
                strokeLinecap="round"
                fill="none"
            />
            <AnimatedPath
                animatedProps={dotProps}
                d={d}
                stroke={color}
                strokeWidth={4}
                strokeLinecap="round"
                strokeDasharray={`${dotSize} ${length}`}
                fill="none"
            />
        </>
    );
};

const AnimatedForkSplit = ({
    height,
    centers,
    loopProgress,
    loopProgressA,
    loopProgressB,
    triggerThreshold,
    dotColor,
    forkRange,
    filledCount,
    groupIndex,
    alwaysAnimateIndices,
}: {
    height: number;
    centers: number[];
    loopProgress?: Animated.SharedValue<number>;
    loopProgressA?: Animated.SharedValue<number>;
    loopProgressB?: Animated.SharedValue<number>;
    triggerThreshold?: number;
    dotColor?: string;
    forkRange?: number;
    filledCount?: Animated.SharedValue<number>;
    groupIndex?: number;
    alwaysAnimateIndices?: number[];
}) => {
    const midY = height / 2;
    const localProgress = useSharedValue(0);

    useEffect(() => {
        if (loopProgress != null) return;
        localProgress.value = withRepeat(
            withTiming(1, {
                duration: 1200,
                easing: Easing.inOut(Easing.ease),
            }),
            -1,
            false,
        );
    }, [loopProgress]);

    const threshold = triggerThreshold ?? 0;
    const range = forkRange ?? Math.max(0.01, 1 - threshold);

    return (
        <Svg
            width={48}
            height={height}
            viewBox={`0 0 48 ${height}`}
            {...{ overflow: 'visible' }}
        >
            {centers.map((yEnd, i) => (
                <AnimatedForkProng
                    key={i}
                    midY={midY}
                    yEnd={yEnd}
                    color={
                        dotColor ??
                        toHex(DOT_COLORS[i % DOT_COLORS.length] ?? '$water')
                    }
                    loopProgress={loopProgress}
                    loopProgressA={loopProgressA}
                    loopProgressB={loopProgressB}
                    localProgress={localProgress}
                    threshold={threshold}
                    range={range}
                    filledCount={filledCount}
                    groupIndex={groupIndex}
                    alwaysAnimate={alwaysAnimateIndices?.includes(i)}
                />
            ))}
        </Svg>
    );
};

export const OnboardingMoneyMapPreviewFork = ({
    height,
    prongs,
    cardCenters,
    animated,
    loopProgress,
    loopProgressA,
    loopProgressB,
    triggerThreshold,
    dotColor,
    forkRange,
    filledCount,
    groupIndex,
    alwaysAnimateIndices,
}: {
    height: number;
    prongs?: 1 | 2 | 3 | 4;
    cardCenters?: number[];
    animated?: boolean;
    loopProgress?: Animated.SharedValue<number>;
    loopProgressA?: Animated.SharedValue<number>;
    loopProgressB?: Animated.SharedValue<number>;
    triggerThreshold?: number;
    dotColor?: string;
    forkRange?: number;
    filledCount?: Animated.SharedValue<number>;
    groupIndex?: number;
    alwaysAnimateIndices?: number[];
}) => {
    if (prongs === 1) {
        if (animated) {
            if (triggerThreshold != null) {
                return (
                    <AnimatedForkSplit
                        height={12}
                        centers={[6]}
                        loopProgress={loopProgress}
                        loopProgressA={loopProgressA}
                        loopProgressB={loopProgressB}
                        triggerThreshold={triggerThreshold}
                        dotColor={dotColor}
                        forkRange={forkRange}
                        filledCount={filledCount}
                        groupIndex={groupIndex}
                        alwaysAnimateIndices={alwaysAnimateIndices}
                    />
                );
            }
            return <AnimatedForkLine loopProgress={loopProgress} />;
        }
        return (
            <View
                backgroundColor="$white"
                width={48}
                height={4}
                borderRadius={4}
            />
        );
    }
    const midY = height / 2;
    const prongCount = prongs ?? 2;
    const defaultCenters = Array.from({ length: prongCount }, (_, i) => {
        const spacing = height / (prongCount + 1);
        return spacing * (i + 1);
    });
    const centers = cardCenters ?? defaultCenters;

    if (animated) {
        return (
            <AnimatedForkSplit
                height={height}
                centers={centers}
                loopProgress={loopProgress}
                loopProgressA={loopProgressA}
                loopProgressB={loopProgressB}
                triggerThreshold={triggerThreshold}
                dotColor={dotColor}
                forkRange={forkRange}
                filledCount={filledCount}
                groupIndex={groupIndex}
                alwaysAnimateIndices={alwaysAnimateIndices}
            />
        );
    }
    return (
        <Svg
            width={48}
            height={height}
            viewBox={`0 0 48 ${height}`}
            {...{ overflow: 'visible' }}
        >
            {centers.map((yEnd, i) => (
                <Path
                    key={i}
                    d={`M 0 ${midY} C 28 ${midY}, 28 ${yEnd}, 48 ${yEnd}`}
                    stroke="white"
                    strokeWidth={4}
                    strokeLinecap="round"
                    fill="none"
                />
            ))}
        </Svg>
    );
};
